import { z } from 'zod';
import { LocalDate, Timestamp } from './common.js';
import { Category } from './enums.js';
/**
 * Rassegne (docs/03-modello-dati.md §2.6): raccolte di eventi che tornano ogni anno nello stesso
 * periodo (Presepi viventi, Sagre d'autunno, Pizzica e Notte della Taranta…). La rassegna dice
 * il periodo e quali eventi ne fanno parte (`criteria`, per l'AI); ogni anno ha la sua edizione,
 * che la redazione attiva quando il bot la propone, e gli eventi di quell'anno. Sul sito una
 * rassegna si vede solo con l'edizione attiva e solo nel suo periodo.
 */
/** Decorazioni stagionali del sito: luci delle feste, autunno, Natale. */
export const RASSEGNA_THEMES = ['luminarie', 'autunno', 'natale'];
/** Estremo del periodo: giorno fisso `MM-DD` o giorni da Pasqua `E-9`, `E+1`. */
export const RassegnaBound = z
    .string()
    .regex(/^((0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])|E[+-]\d{1,3})$/, 'giorno nel formato MM-DD, o giorni da Pasqua come E-9 o E+1');
const RassegnaFields = z.object({
    slug: z
        .string()
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'slug in kebab-case minuscolo')
        .max(40),
    title: z.string().trim().min(3).max(80),
    /** sottotitolo pubblico (elenco e pagina della rassegna) */
    intro: z.string().trim().max(300).default(''),
    /** testo pubblico della pagina, in markdown */
    description: z.string().trim().max(4000).default(''),
    /** per l'AI: quali eventi ne fanno parte (mai pubblicato) */
    criteria: z.string().trim().min(10).max(2000),
    from: RassegnaBound,
    to: RassegnaBound,
    /** giorni prima dell'inizio in cui il bot propone di attivare l'edizione */
    notifyDays: z.int().min(0).max(60).default(10),
    /** solo eventi di queste categorie (principale o secondarie); vuoto: tutte */
    categories: z.array(Category).max(16).default([]),
    /** spenta: niente proposte né nuove edizioni */
    enabled: z.boolean().default(true),
    /** decorazione del sito durante l'edizione in corso (fascia sotto l'intestazione) */
    theme: z.enum(RASSEGNA_THEMES).optional(),
});
const MAX_DAYS = 200;
/** Periodo valido: inizio prima della fine e non oltre MAX_DAYS giorni, per i prossimi anni. */
export function periodProblem(r) {
    for (let y = 2026; y < 2036; y++) {
        const { start, end } = editionRange(r, y);
        const days = (Date.parse(end) - Date.parse(start)) / 86_400_000;
        if (days < 0)
            return `nel ${y} la fine (${end}) viene prima dell'inizio (${start})`;
        if (days > MAX_DAYS)
            return `periodo troppo lungo (${Math.round(days)} giorni, al massimo ${MAX_DAYS})`;
    }
    return null;
}
export const RassegnaInput = RassegnaFields.superRefine((r, ctx) => {
    const p = periodProblem(r);
    if (p)
        ctx.addIssue({ code: 'custom', path: ['to'], message: p });
});
export const Rassegna = RassegnaFields.extend({
    createdAt: Timestamp,
    updatedAt: Timestamp,
    version: z.int().min(1),
});
/** proposta dal bot, attiva (visibile nel periodo), saltata quest'anno */
export const EditionStatus = z.enum(['proposed', 'active', 'skipped']);
/** Edizione di un anno: date concrete del periodo, calcolate quando si crea o si cambia. */
export const RassegnaEdition = z.object({
    slug: z.string(),
    year: z.int(),
    status: EditionStatus,
    start: LocalDate,
    end: LocalDate,
    notifiedAt: Timestamp.optional(),
    activatedAt: Timestamp.optional(),
    by: z.object({ sub: z.string(), email: z.string().optional() }).optional(),
    /** ultima ricerca degli eventi con l'AI */
    populatedAt: Timestamp.optional(),
    populate: z.object({ checked: z.int(), added: z.int(), error: z.string().optional() }).optional(),
});
/** Evento di un'edizione; `excluded`: tolto dalla redazione, l'AI non lo ripropone. */
export const RassegnaMember = z.object({
    slug: z.string(),
    year: z.int(),
    eventId: z.string(),
    eventSlug: z.string(),
    title: z.string(),
    addedBy: z.enum(['ai', 'admin']),
    reason: z.string().max(300).optional(),
    at: Timestamp,
    excluded: z.boolean().optional(),
});
// --- periodi ----------------------------------------------------------------------------------
/** Domenica di Pasqua (calendario gregoriano, algoritmo di Meeus/Jones/Butcher). */
export function easterDate(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
const addDays = (d, n) => {
    const x = new Date(`${d}T12:00:00Z`);
    x.setUTCDate(x.getUTCDate() + n);
    return x.toISOString().slice(0, 10);
};
const leap = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
function boundDate(b, year) {
    if (b.startsWith('E'))
        return addDays(easterDate(year), Number(b.slice(1)));
    // 29 febbraio negli anni non bisestili: 28
    return b === '02-29' && !leap(year) ? `${year}-02-28` : `${year}-${b}`;
}
/**
 * Date dell'edizione `year` (l'anno in cui comincia): un periodo a giorni fissi che finisce
 * prima di cominciare passa l'anno (8 dicembre – 6 gennaio).
 */
export function editionRange(r, year) {
    const start = boundDate(r.from, year);
    let end = boundDate(r.to, year);
    if (end < start && !r.to.startsWith('E'))
        end = boundDate(r.to, year + 1);
    return { start, end };
}
/** Anno dell'edizione in corso il giorno `today`, o null fuori periodo. */
export function currentEditionYear(r, today) {
    const y = Number(today.slice(0, 4));
    for (const year of [y, y - 1]) {
        const { start, end } = editionRange(r, year);
        if (start <= today && today <= end)
            return year;
    }
    return null;
}
/**
 * Edizione da proporre il giorno `today`: dai `notifyDays` giorni prima dell'inizio fino alla
 * fine (se la proposta arriva tardi, vale ancora finché il periodo non è finito).
 */
export function dueEditionYear(r, today) {
    const y = Number(today.slice(0, 4));
    for (const year of [y - 1, y, y + 1]) {
        const { start, end } = editionRange(r, year);
        if (addDays(start, -r.notifyDays) <= today && today <= end)
            return year;
    }
    return null;
}
// --- rassegne suggerite -----------------------------------------------------------------------
/** Rassegne di partenza nei vari periodi dell'anno (pannello: "Aggiungi le rassegne suggerite"). */
export const RASSEGNE_SUGGERITE = [
    {
        slug: 'focare-di-sant-antonio',
        title: 'Focare e fuochi di Sant’Antonio',
        intro: 'A metà gennaio il Salento accende i suoi falò per Sant’Antonio Abate.',
        description: 'Dalla Focara di Novoli, il falò più grande del Mediterraneo, alle piccole focare di paese: musica, fuochi d’artificio, benedizione degli animali e cibo di strada intorno al fuoco.',
        criteria: 'Focare, falò e fuochi per Sant’Antonio Abate (17 gennaio), come la Focara di Novoli; feste di Sant’Antonio Abate con fuochi, benedizione degli animali, fiere e musica legate alla festa.',
        from: '01-10',
        to: '01-20',
    },
    {
        slug: 'tavole-di-san-giuseppe',
        title: 'Tavole di San Giuseppe',
        intro: 'Le tavole votive, i fuochi e le fiere della festa di San Giuseppe.',
        description: 'Intorno al 19 marzo in tanti paesi del Salento si preparano le tavole di San Giuseppe: banchetti votivi con tredici o più pietanze, la “massa”, le zeppole, i fuochi e le fiere di primavera.',
        criteria: 'Tavole di San Giuseppe (tavole votive, “massa”, visita delle tavole), fuochi e falò di San Giuseppe, feste e fiere per San Giuseppe (19 marzo), sagre delle zeppole legate alla festa.',
        from: '03-10',
        to: '03-21',
    },
    {
        slug: 'settimana-santa',
        title: 'Settimana Santa e Pasqua',
        intro: 'Riti, processioni e Passioni viventi dalla Domenica delle Palme a Pasquetta.',
        description: 'Le processioni dei Misteri, le Via Crucis e le Passioni viventi, i concerti di musica sacra e le tradizioni di Pasqua e Pasquetta nei paesi del Salento.',
        criteria: 'Riti della Settimana Santa: processioni dei Misteri e del Venerdì Santo, Via Crucis e Passioni viventi, rappresentazioni sacre, concerti di musica sacra; eventi di Pasqua e Pasquetta (scampagnate, fiere, feste del Lunedì dell’Angelo).',
        from: 'E-9',
        to: 'E+1',
    },
    {
        slug: 'feste-patronali-e-luminarie',
        theme: 'luminarie',
        title: 'Feste patronali e luminarie',
        intro: 'L’estate delle feste di paese: luminarie, bande, processioni e fuochi.',
        description: 'Le feste dei santi patroni sono il cuore dell’estate salentina: luminarie artistiche, concerti bandistici in cassa armonica, processioni, fiere e fuochi d’artificio.',
        criteria: 'Feste patronali e religiose d’estate con luminarie, bande, processioni, cassa armonica, fuochi d’artificio e fiere del santo patrono.',
        from: '06-20',
        to: '09-20',
        categories: ['FESTE_PATRONALI'],
    },
    {
        slug: 'concerti-d-estate',
        theme: 'luminarie',
        title: 'Concerti d’estate',
        intro: 'Musica dal vivo all’aperto: piazze, castelli, masserie e spiagge.',
        description: 'Concerti in piazza e nei castelli, festival musicali, jazz, cantautori e grandi nomi: tutta la musica dal vivo dell’estate nel Salento.',
        criteria: 'Concerti e spettacoli di musica dal vivo d’estate: festival musicali, concerti in piazza, nei castelli, nelle masserie o in spiaggia, jazz, rock, pop, musica classica, cantautori. Escludi le sagre dove la musica è solo di contorno.',
        from: '06-15',
        to: '09-15',
        categories: ['CONCERTI', 'FESTIVAL_CULTURALI', 'EVENTI_DI_DANZA'],
    },
    {
        slug: 'pizzica-e-notte-della-taranta',
        theme: 'luminarie',
        title: 'Pizzica e Notte della Taranta',
        intro: 'Le tappe del Festival itinerante, il Concertone e le notti di pizzica.',
        description: 'Da metà luglio alla fine di agosto il Salento balla la pizzica: le tappe del Festival itinerante della Notte della Taranta, il Concertone di Melpignano, ronde e serate di musica popolare.',
        criteria: 'Tappe del Festival itinerante della Notte della Taranta e Concertone di Melpignano; serate, concerti e festival di pizzica, tarantella e musica popolare salentina; ronde e corsi di pizzica.',
        from: '07-15',
        to: '08-31',
    },
    {
        slug: 'ferragosto-nel-salento',
        theme: 'luminarie',
        title: 'Ferragosto nel Salento',
        intro: 'Le feste dell’Assunta, i fuochi e le serate dal 10 al 17 agosto.',
        description: 'La settimana di Ferragosto tra feste dell’Assunta, sagre, concerti e fuochi d’artificio sul mare.',
        criteria: 'Eventi della settimana di Ferragosto (14-16 agosto in particolare): feste della Madonna Assunta, fuochi d’artificio, sagre, concerti e serate di Ferragosto.',
        from: '08-10',
        to: '08-17',
    },
    {
        slug: 'sagre-d-autunno',
        theme: 'autunno',
        title: 'Sagre d’autunno',
        intro: 'Castagne, funghi, zucche, olio nuovo e vino novello.',
        description: 'L’autunno salentino si festeggia a tavola: sagre della castagna e dei funghi, feste della zucca, frantoi aperti con l’olio nuovo e il vino novello.',
        criteria: 'Sagre e feste di prodotti d’autunno: castagne e caldarroste, funghi, zucca, olio nuovo e frantoi aperti, vino novello, melagrane, uva e vendemmia, piatti della tradizione autunnale.',
        from: '09-20',
        to: '11-30',
        categories: ['SAGRE', 'CIBO_VINO', 'FIERE', 'EVENTI'],
    },
    {
        slug: 'oktoberfest',
        title: 'Oktoberfest nel Salento',
        intro: 'Birra, wurstel e musica: le feste della birra tra settembre e ottobre.',
        description: 'Anche il Salento ha le sue Oktoberfest: feste della birra con birrifici artigianali e boccali in tavola, stinco, wurstel e bretzel, musica bavarese, rock e cover band, dalle piazze ai pub e alle masserie.',
        criteria: 'Oktoberfest e feste della birra: eventi che si chiamano Oktoberfest o festa/sagra della birra, beer fest e festival della birra artigianale, serate con birra e cucina bavarese (wurstel, stinco, bretzel). Non basta una sagra o un concerto in cui si serve birra.',
        from: '09-15',
        to: '10-31',
    },
    {
        slug: 'halloween',
        theme: 'autunno',
        title: 'Halloween e feste delle zucche',
        intro: 'Feste in maschera, zucche intagliate e serate da brivido.',
        description: 'Feste di Halloween per grandi e piccoli, laboratori di zucche, percorsi da brivido e serate a tema tra fine ottobre e i primi di novembre.',
        criteria: 'Eventi di Halloween: feste in maschera, serate a tema, percorsi e case stregate, laboratori di zucche per bambini, feste della zucca di fine ottobre.',
        from: '10-24',
        to: '11-02',
    },
    {
        slug: 'san-martino-e-vino-novello',
        theme: 'autunno',
        title: 'San Martino e vino novello',
        intro: 'Vino nuovo, castagne e caldarroste per l’estate di San Martino.',
        description: 'Intorno all’11 novembre cantine e piazze festeggiano San Martino: vino novello, caldarroste, pittule e musica popolare.',
        criteria: 'Feste di San Martino (11 novembre), vino novello, cantine aperte, castagne e caldarroste, pittule e serate legate alla festa di San Martino.',
        from: '11-01',
        to: '11-16',
    },
    {
        slug: 'mercatini-di-natale',
        theme: 'natale',
        title: 'Mercatini di Natale',
        intro: 'Bancarelle, artigianato e villaggi di Babbo Natale.',
        description: 'I mercatini natalizi del Salento: artigianato, idee regalo, dolci della tradizione, villaggi di Babbo Natale e piste di pattinaggio.',
        criteria: 'Mercatini di Natale e fiere natalizie, villaggi di Babbo Natale, case di Babbo Natale, piste di pattinaggio, mercatini dell’artigianato e dei regali di Natale.',
        from: '11-25',
        to: '01-06',
        categories: ['MERCATINI', 'EVENTI_NATALIZI', 'FIERE', 'EVENTI'],
    },
    {
        slug: 'presepi-viventi',
        theme: 'natale',
        title: 'Presepi viventi',
        intro: 'La Natività in costume nei borghi, nelle grotte e nei frantoi ipogei.',
        description: 'Dall’Immacolata all’Epifania i paesi del Salento si trasformano in Betlemme: presepi viventi tra antichi mestieri, grotte e frantoi ipogei, e mostre di presepi artistici.',
        criteria: 'Presepi viventi, Natività in costume, rievocazioni della nascita di Gesù, mostre di presepi artistici e presepi visitabili.',
        from: '12-08',
        to: '01-06',
    },
    {
        slug: 'capodanno-in-piazza',
        theme: 'natale',
        title: 'Capodanno in piazza',
        intro: 'Concerti, feste e fuochi per salutare il nuovo anno.',
        description: 'Le feste di fine anno nelle piazze del Salento: concerti di San Silvestro, conto alla rovescia, fuochi d’artificio e il primo giorno dell’anno.',
        criteria: 'Eventi della notte di San Silvestro e di Capodanno: concerti e feste in piazza, veglioni, conto alla rovescia, fuochi d’artificio, concerti del primo gennaio.',
        from: '12-28',
        to: '01-01',
    },
];
//# sourceMappingURL=rassegna.js.map